import {
  ReactElement,
  ReactNode,
  createContext,
  useContext,
  useMemo,
} from 'react';
import {
  Machine,
  MachineConnectionType,
  useGetMachineQuery,
} from '../../api/graphql/generated';
import {
  AppMode,
  useAppModeContext,
} from '../../components/app/app-mode-provider';
import { ScratchpadPlaylistContextProvider } from '../../components/machine/playlist/scratchpad-playlist.hooks';
import { RobotManufacturerProvider } from './robot-manufacturer-context';

export interface MachineContextValue {
  machine: Machine;
  isDirectConnect: boolean;
  hideAttachments?: boolean;
  hideRecommendations?: boolean;
}

export const MachineContext = createContext<MachineContextValue | undefined>(
  undefined
);

export interface MachineContextProviderProps {
  machineId: string;
  teamId: string;
  children: ReactNode;
}

export const MachineContextProvider = (
  props: MachineContextProviderProps
): ReactElement | null => {
  const { machineId, teamId, children } = props;

  const { mode } = useAppModeContext();

  const { data: getMachineData } = useGetMachineQuery({
    variables: { input: { id: machineId, teamId } },
    skip: machineId === 'new',
  });

  const machine = getMachineData?.getMachine;

  const contextValue = useMemo<MachineContextValue | undefined>(() => {
    if (!machine) {
      return;
    }

    const isDirectConnect =
      machine.connectionType === MachineConnectionType.DIRECT;
    const isKioskMode = mode === AppMode.KIOSK;

    return {
      machine,
      isDirectConnect,
      hideAttachments: isKioskMode,
      hideRecommendations: isKioskMode,
    };
  }, [machine, mode]);

  if (contextValue) {
    return (
      <MachineContext.Provider value={contextValue}>
        <RobotManufacturerProvider>
          <ScratchpadPlaylistContextProvider machineId={machineId}>
            {children}
          </ScratchpadPlaylistContextProvider>
        </RobotManufacturerProvider>
      </MachineContext.Provider>
    );
  }

  return null;
};

export const useMachineContext = (): MachineContextValue => {
  const context = useContext(MachineContext);
  return context as MachineContextValue;
};

export const useIsMachineActive = (): boolean => {
  const context = useContext(MachineContext);
  const active = context?.machine?.active;
  return active ? true : false;
};
